import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";

// Monday-start week key + label
function weekStart(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7; // 0 = Monday
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}

export function MoodChart({ entries, moods }) {
  const moodById = Object.fromEntries(moods.map((m) => [m.id, m]));
  const withMood = entries.filter((e) => e.mood && moodById[e.mood]);
  if (withMood.length < 2) return null;

  // group by week
  const buckets = {};
  withMood.forEach((e) => {
    const ws = weekStart(e.created_at);
    const key = ws.toISOString().slice(0, 10);
    if (!buckets[key]) buckets[key] = { key, date: ws, sum: 0, count: 0, moodCounts: {} };
    const m = moodById[e.mood];
    buckets[key].sum += m.score;
    buckets[key].count += 1;
    buckets[key].moodCounts[e.mood] = (buckets[key].moodCounts[e.mood] || 0) + 1;
  });

  const data = Object.values(buckets)
    .sort((a, b) => a.date - b.date)
    .slice(-8)
    .map((b) => {
      const dominantId = Object.entries(b.moodCounts).sort((x, y) => y[1] - x[1])[0][0];
      return {
        label: b.date.toLocaleDateString("it-IT", { day: "2-digit", month: "short" }),
        score: Math.round(b.sum / b.count),
        dominant: moodById[dominantId].label,
        count: b.count,
      };
    });

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload;
    return (
      <div className="rounded-xl bg-[#2A0A1B] text-white px-3 py-2 text-xs shadow-lg border border-[#E32C8C]/40">
        <p className="font-semibold">Settimana del {p.label}</p>
        <p className="text-white/85 mt-0.5">Umore: {p.score}/100</p>
        <p className="text-white/85">Prevale: {p.dominant}</p>
        <p className="text-white/85">{p.count} {p.count === 1 ? "voce" : "voci"}</p>
      </div>
    );
  };

  return (
    <div className="rounded-2xl bg-[#2A0A1B] border border-[#E32C8C]/35 bw-card p-4 mb-6" data-testid="mood-chart">
      <div className="flex items-center gap-2 mb-1">
        <TrendingUp className="w-4 h-4 text-[#E32C8C]" />
        <p className="text-xs font-semibold text-[#EAC6D9] uppercase tracking-wider">Andamento umore</p>
      </div>
      <p className="text-sm text-[#EAC6D9] mb-3">Settimana per settimana</p>
      <div style={{ width: "100%", height: 180 }}>
        <ResponsiveContainer>
          <AreaChart data={data} margin={{ top: 8, right: 8, left: -22, bottom: 0 }}>
            <defs>
              <linearGradient id="moodGrad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#E32C8C" stopOpacity={0.55} />
                <stop offset="100%" stopColor="#E32C8C" stopOpacity={0.03} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#6B2247" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" tick={{ fill: "#EAC6D9", fontSize: 11 }} axisLine={false} tickLine={false} />
            <YAxis domain={[0, 100]} ticks={[0, 50, 100]} tick={{ fill: "#EAC6D9", fontSize: 11 }} axisLine={false} tickLine={false} />
            <Tooltip content={<CustomTooltip />} />
            <Area type="monotone" dataKey="score" stroke="#8E1A52" strokeWidth={2.5} fill="url(#moodGrad)" dot={{ fill: "#E32C8C", r: 3 }} activeDot={{ r: 5 }} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
